import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Loader2, ChevronDown } from "lucide-react"
import { toast } from "sonner"

import { useQueryClient } from "@tanstack/react-query"
import { useCreateProperty, useUpdateProperty } from "@/features/properties/hooks/useProperties"
import { propertiesService } from "@/features/properties/services/propertiesService"
import { PhotoGrid, PendingPhotoGrid, PendingDocList, UploadedDocList } from "./PhotoGrid"
import { COUNTRIES, CURRENCIES, PROPERTY_TYPES, PROPERTY_STATUSES } from "@/lib/constants"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import type { Property, PropertyPhoto, Document } from "@rentbase/shared"

const schema = z.object({
  name: z.string().min(1, "Property name is required"),
  type: z.string().optional(),
  address_line1: z.string().optional(),
  city: z.string().optional(),
  postcode: z.string().optional(),
  country_code: z.string().optional(),
  size_sqm: z.coerce.number().optional(),
  bedrooms: z.coerce.number().optional(),
  bathrooms: z.coerce.number().optional(),
  currency: z.string().optional(),
  status: z.string().optional(),
  notes: z.string().optional(),
  furnished: z.string().optional(),
  parking_spaces: z.coerce.number().optional(),
  pets_allowed: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

interface PropertyFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  property?: Property
}

export function PropertyForm({ open, onOpenChange, onSuccess, property }: PropertyFormProps) {
  const isEdit = !!property
  const queryClient = useQueryClient()
  const createProperty = useCreateProperty()
  const updateProperty = useUpdateProperty()

  // Photo state
  const [pendingPhotos, setPendingPhotos] = useState<File[]>([])
  const [existingPhotos, setExistingPhotos] = useState<PropertyPhoto[]>([])

  // Document state
  const [pendingDocs, setPendingDocs] = useState<File[]>([])
  const [existingDocs, setExistingDocs] = useState<Document[]>([])
  const [loadingDocs, setLoadingDocs] = useState(false)

  const [uploading, setUploading] = useState(false)
  const [photosOpen, setPhotosOpen] = useState(false)
  const [docsOpen, setDocsOpen] = useState(false)
  const isPending = createProperty.isPending || updateProperty.isPending || uploading

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      type: "",
      address_line1: "",
      city: "",
      postcode: "",
      country_code: "",
      currency: "EUR",
      status: "available",
      notes: "",
      furnished: "",
      pets_allowed: "",
    },
  })

  useEffect(() => {
    if (!open) return

    if (property) {
      form.reset({
        name: property.name ?? "",
        type: property.type ?? "",
        address_line1: property.address_line1 ?? "",
        city: property.city ?? "",
        postcode: property.postcode ?? "",
        country_code: property.country_code ?? "",
        size_sqm: (property as unknown as { size_sqm?: number }).size_sqm,
        bedrooms: property.bedrooms ?? undefined,
        bathrooms: property.bathrooms ?? undefined,
        currency: property.currency ?? "EUR",
        status: property.status ?? "available",
        notes: property.notes ?? "",
        furnished: property.furnished ?? "",
        parking_spaces: property.parking_spaces,
        pets_allowed: property.pets_allowed ?? "",
      })
      setExistingPhotos(property.photos ?? [])
      setPendingPhotos([])
      setPendingDocs([])
      setPhotosOpen((property.photos?.length ?? 0) > 0)
      setDocsOpen(false)

      // Load documents for this property
      setLoadingDocs(true)
      propertiesService.listDocuments(property.id).then((docs) => {
        setExistingDocs(docs)
      }).catch(() => {
        toast.error("Failed to load documents")
      }).finally(() => setLoadingDocs(false))
    } else {
      form.reset({
        name: "",
        type: "",
        address_line1: "",
        city: "",
        postcode: "",
        country_code: "",
        currency: "EUR",
        status: "available",
        notes: "",
        furnished: "",
        pets_allowed: "",
      })
      setPendingPhotos([])
      setPendingDocs([])
      setExistingPhotos([])
      setExistingDocs([])
    }
  }, [property, open, form])

  async function onSubmit(values: FormValues) {
    const payload = Object.fromEntries(
      Object.entries(values).filter(([, v]) => v !== "" && v !== undefined && v !== null)
    )

    if (isEdit && property) {
      updateProperty.mutate(
        { id: property.id, input: payload },
        {
          onSuccess: () => {
            toast.success("Property updated")
            onSuccess()
            onOpenChange(false)
          },
          onError: (err: unknown) => {
            toast.error(err instanceof Error ? err.message : "Failed to update property")
          },
        }
      )
    } else {
      createProperty.mutate(payload, {
        onSuccess: async (created) => {
          // Upload pending photos and docs after property is created
          if (pendingPhotos.length > 0 || pendingDocs.length > 0) {
            setUploading(true)
            try {
              await Promise.all([
                pendingPhotos.length > 0
                  ? propertiesService.uploadPhotos(created.id, pendingPhotos)
                  : Promise.resolve(),
                ...pendingDocs.map((f) => propertiesService.uploadDocument(created.id, f)),
              ])
              if (pendingDocs.length > 0) {
                queryClient.invalidateQueries({ queryKey: ["plan", "usage"] })
              }
            } catch {
              toast.error("Property created, but some files failed to upload")
            } finally {
              setUploading(false)
            }
          }
          toast.success("Property created")
          onSuccess()
          onOpenChange(false)
        },
        onError: (err: unknown) => {
          toast.error(err instanceof Error ? err.message : "Failed to create property")
        },
      })
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[92dvh] overflow-y-auto rounded-t-2xl">
        <SheetHeader className="mb-4">
          <SheetTitle>{isEdit ? "Edit Property" : "Add Property"}</SheetTitle>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pb-8">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Property Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. 42 Oak Street" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PROPERTY_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address_line1"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 1</FormLabel>
                  <FormControl>
                    <Input placeholder="Street address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input placeholder="City" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="postcode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Postcode</FormLabel>
                    <FormControl>
                      <Input placeholder="Postcode" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="country_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-3">
              <FormField
                control={form.control}
                name="size_sqm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Size m²</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="bedrooms"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bedrooms</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="bathrooms"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bathrooms</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Currency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PROPERTY_STATUSES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="furnished"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Furnished</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select option" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="parking_spaces"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Parking Spaces</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="pets_allowed"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pets Allowed</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="negotiable">Negotiable</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Any additional notes..."
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Photos section */}
            <div className="border border-border rounded-xl overflow-hidden">
              <button
                type="button"
                className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium hover:bg-muted/50 transition-colors"
                onClick={() => setPhotosOpen((v) => !v)}
              >
                <span>
                  Photos
                  {isEdit
                    ? existingPhotos.length > 0 ? ` (${existingPhotos.length})` : ""
                    : pendingPhotos.length > 0 ? ` (${pendingPhotos.length})` : ""}
                </span>
                <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", photosOpen && "rotate-180")} />
              </button>
              {photosOpen && (
                <div className="px-3 pb-3 pt-1">
                  {isEdit && property ? (
                    <PhotoGrid
                      propertyId={property.id}
                      photos={existingPhotos}
                      onChange={setExistingPhotos}
                    />
                  ) : (
                    <PendingPhotoGrid files={pendingPhotos} onChange={setPendingPhotos} />
                  )}
                </div>
              )}
            </div>

            {/* Documents section */}
            <div className="border border-border rounded-xl overflow-hidden">
              <button
                type="button"
                className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium hover:bg-muted/50 transition-colors"
                onClick={() => setDocsOpen((v) => !v)}
              >
                <span>
                  Documents
                  {isEdit
                    ? existingDocs.length > 0 ? ` (${existingDocs.length})` : ""
                    : pendingDocs.length > 0 ? ` (${pendingDocs.length})` : ""}
                </span>
                <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", docsOpen && "rotate-180")} />
              </button>
              {docsOpen && (
                <div className="px-3 pb-3 pt-1">
                  {isEdit && property ? (
                    loadingDocs ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <UploadedDocList
                        propertyId={property.id}
                        docs={existingDocs}
                        onChange={setExistingDocs}
                      />
                    )
                  ) : (
                    <PendingDocList files={pendingDocs} onChange={setPendingDocs} />
                  )}
                </div>
              )}
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-xl"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {uploading ? "Uploading files..." : "Saving..."}
                </>
              ) : (
                "Save Property"
              )}
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}
